import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Building2, Search, Pencil, Save, ArrowLeft, TrendingUp, Wallet, FileText, Calendar } from 'lucide-react';
import { tesisTuruText } from '../utils/tesisTuru';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTL = (val) => {
  const n = parseFloat(val);
  if (isNaN(n) || n === 0) return '-';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('tr-TR');
  } catch { return iso; }
};

const KamulastirmaIslemleriPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_admin && !user?.has_emlak_kamulastirma_access) {
      navigate('/dashboard');
      return;
    }
    fetchData();
    // eslint-disable-next-line
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Tüm emlak kayıtlarını al, sonra mülkiyet = il_mudurlugu_kamulastirma olanları filtrele
      const res = await axios.get(`${API}/emlak-islemleri`, { withCredentials: true });
      const all = Array.isArray(res.data) ? res.data : (res.data.items || []);
      setItems(all.filter(e => e.mulkiyet === 'il_mudurlugu_kamulastirma'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter(e =>
      (e.tesis_adi || '').toLowerCase().includes(s) ||
      (e.ilce || '').toLowerCase().includes(s) ||
      (e.mahalle || '').toLowerCase().includes(s) ||
      (e.ada || '').toString().includes(s) ||
      (e.parsel || '').toString().includes(s) ||
      (e.deger_tespit_sirketi || '').toLowerCase().includes(s)
    );
  }, [items, search]);

  // Özet istatistikler
  const stats = useMemo(() => {
    const sum = (k) => items.reduce((acc, it) => acc + (parseFloat(it[k]) || 0), 0);
    return {
      toplamKayit: items.length,
      toplamDegerTespit: sum('deger_tespit_bedeli'),
      toplamKomisyonTespit: sum('komisyon_tespit_bedeli'),
      toplamUzlasma: sum('uzlasma_bedeli'),
    };
  }, [items]);

  const openEdit = (it) => {
    setEditItem(it);
    setEditForm({
      kamulastirma_tarihi: it.kamulastirma_tarihi || '',
      deger_tespit_sirketi: it.deger_tespit_sirketi || '',
      deger_tespit_bedeli: it.deger_tespit_bedeli || '',
      komisyon_tespit_bedeli: it.komisyon_tespit_bedeli || '',
      uzlasma_bedeli: it.uzlasma_bedeli || '',
    });
  };

  const saveEdit = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      // Mevcut tüm alanları koru, sadece kamulaştırma alanlarını güncelle
      const payload = { ...editItem, ...editForm };
      delete payload._id;
      await axios.put(`${API}/emlak-islemleri/${editItem.id}`, payload, { withCredentials: true });
      setEditItem(null);
      fetchData();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/emlak-kamulastirma')} variant="ghost" size="sm" className="text-gray-300 hover:text-white" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Geri
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">🏛️ Kamulaştırma İşlemleri</h1>
                <p className="text-xs text-gray-400">İl Müdürlüğü tarafından kamulaştırılan taşınmazlar</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-300 hover:text-white">Dashboard</Button>
              <Button onClick={() => { logout(); navigate('/login'); }} variant="ghost" className="text-red-400 hover:text-red-300">Çıkış</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6" data-testid="kamulastirma-islemleri-page">
        {/* Özet Kartları */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-amber-900/20 border-amber-700/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-4 h-4 text-amber-300" />
                <span className="text-xs text-amber-300">Toplam Kayıt</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.toplamKayit}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-900/20 border-blue-700/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-blue-300" />
                <span className="text-xs text-blue-300">Toplam Değer Tespit</span>
              </div>
              <p className="text-lg font-bold text-white">{formatTL(stats.toplamDegerTespit)}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-900/20 border-purple-700/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-purple-300" />
                <span className="text-xs text-purple-300">Toplam Komisyon Tespit</span>
              </div>
              <p className="text-lg font-bold text-white">{formatTL(stats.toplamKomisyonTespit)}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-900/20 border-green-700/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4 text-green-300" />
                <span className="text-xs text-green-300">Toplam Uzlaşma</span>
              </div>
              <p className="text-lg font-bold text-white">{formatTL(stats.toplamUzlasma)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Arama */}
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tesis adı, ilçe, mahalle, ada/parsel, değer tespit şirketi..."
            className="pl-9 bg-gray-800 border-gray-700 text-white"
            data-testid="kamulastirma-search-input"
          />
        </div>

        {/* Liste */}
        {loading ? (
          <div className="text-gray-400 text-center py-8">Yükleniyor...</div>
        ) : filtered.length === 0 ? (
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">
                {search ? 'Aramayla eşleşen kayıt yok.' : 'Henüz kamulaştırma kaydı yok. Bir taşınmazın mülkiyetini "İL MÜDÜRLÜĞÜ (KAMULAŞTIRMA)" olarak işaretlediğinizde burada görünecektir.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="kamulastirma-list">
            {filtered.map((it) => (
              <Card key={it.id} className="bg-gray-900/70 border-gray-700/50 hover:border-amber-600/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-amber-700/80 text-white text-[10px]">{it.tasinmaz_turu === 'yapi' ? 'YAPI' : 'ARSA'}</Badge>
                        {tesisTuruText(it.tesis_turu) && <Badge className="bg-gray-700 text-white text-[10px]">{tesisTuruText(it.tesis_turu)}</Badge>}
                      </div>
                      <h3 className="text-base font-semibold text-white truncate" title={it.tesis_adi || `${it.ilce} - ${it.mahalle}`}>
                        {it.tesis_adi || `${it.ilce || ''} ${it.mahalle ? '— ' + it.mahalle : ''}`}
                      </h3>
                      <p className="text-xs text-gray-400 mt-1">
                        {it.ilce} {it.mahalle && `· ${it.mahalle}`}
                        {it.ada && it.parsel && ` · Ada ${it.ada} / Parsel ${it.parsel}`}
                      </p>
                    </div>
                    {!user?.is_viewer && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(it)} className="text-amber-300 hover:text-amber-200 h-8" data-testid={`edit-kamulastirma-${it.id}`}>
                        <Pencil className="w-4 h-4 mr-1" /> Düzenle
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <span className="text-gray-500">Kamulaştırma Tarihi:</span>
                      <p className="text-white font-medium">{formatDate(it.kamulastirma_tarihi)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Değer Tespit Şirketi:</span>
                      <p className="text-white font-medium truncate" title={it.deger_tespit_sirketi}>{it.deger_tespit_sirketi || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Değer Tespit Bedeli:</span>
                      <p className="text-blue-300 font-semibold">{formatTL(it.deger_tespit_bedeli)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Komisyon Tespit Bedeli:</span>
                      <p className="text-purple-300 font-semibold">{formatTL(it.komisyon_tespit_bedeli)}</p>
                    </div>
                    <div className="col-span-2 pt-2 border-t border-gray-800">
                      <span className="text-gray-500">Uzlaşma Bedeli:</span>
                      <p className="text-green-300 font-bold text-base">{formatTL(it.uzlasma_bedeli)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Düzenleme Modal */}
      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="edit-kamulastirma-title">Kamulaştırma Bilgilerini Düzenle</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-3 py-2">
              <p className="text-xs text-gray-400 pb-2 border-b border-gray-800">
                {editItem.tesis_adi || `${editItem.ilce} - ${editItem.mahalle}`}
              </p>
              <div className="space-y-2">
                <Label>Kamulaştırma Tarihi</Label>
                <Input type="date" value={editForm.kamulastirma_tarihi} onChange={(e) => setEditForm({...editForm, kamulastirma_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Değer Tespiti Yapan Şirket</Label>
                <Input value={editForm.deger_tespit_sirketi} onChange={(e) => setEditForm({...editForm, deger_tespit_sirketi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Değer Tespit Bedeli (₺)</Label>
                  <Input type="number" step="0.01" value={editForm.deger_tespit_bedeli} onChange={(e) => setEditForm({...editForm, deger_tespit_bedeli: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label>Komisyon Tespit Bedeli (₺)</Label>
                  <Input type="number" step="0.01" value={editForm.komisyon_tespit_bedeli} onChange={(e) => setEditForm({...editForm, komisyon_tespit_bedeli: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Uzlaşma Bedeli (₺)</Label>
                <Input type="number" step="0.01" value={editForm.uzlasma_bedeli} onChange={(e) => setEditForm({...editForm, uzlasma_bedeli: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditItem(null)} className="text-gray-400">Vazgeç</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-kamulastirma-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KamulastirmaIslemleriPage;
